import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useRef } from "react";

import { UpdatedStamp } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

import { getUnits, messageFor, UnitsSoldError } from "./api";
import { BreakoutBody } from "./TrafficPage";
import { ChannelRefocusContext } from "./components/ChannelPicker";
import { BreakoutSkeleton } from "./components/LoadingSkeletons";
import { NotComputedCard } from "./components/NotComputedCard";
import { ModuleControls, useControls, type Controls } from "./controls";
import { channelLabel } from "./format";
import { OverviewTab } from "./OverviewTab";
import { RegionTab } from "./RegionTab";

export function UnitsPage({ areaName }: { areaName: string }) {
  const controls = useControls();
  const channelRefocus = useRef(false);
  const filtered = Boolean(controls.filter.product || controls.filter.channel.length || controls.filter.source);
  const page = !filtered
    ? controls.filter.region === "overview" ? <OverviewTab areaName={areaName} /> : <RegionTab region={controls.filter.region} areaName={areaName} />
    : <FilteredUnits areaName={areaName} controls={controls} />;
  return <ChannelRefocusContext.Provider value={channelRefocus}>{page}</ChannelRefocusContext.Provider>;
}

function FilteredUnits({ areaName, controls }: { areaName: string; controls: Controls }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const result = useQuery({ queryKey: ["meteoric_units_sold", "units", controls.toQuery()], queryFn: () => getUnits(controls.toQuery()) });
  const labels = [controls.filter.product, ...controls.channels.map(channelLabel)].filter(Boolean).join(" · ");
  const clear = () => controls.setFilter({ ...controls.filter, product: null, channel: [], source: null });
  const data = result.data?.data;
  const title = controls.filter.source ? `Ordered units${labels ? ` for ${labels}` : ""}` : `Units breakout for ${labels}`;
  return <div className="flex flex-col gap-6"><div className="flex flex-wrap items-center justify-between gap-3"><ModuleControls controls={controls} />{data && <UpdatedStamp generatedAt={data.generated_at} />}</div>
    {result.isPending ? <BreakoutSkeleton /> : result.isError ? result.error instanceof UnitsSoldError && result.error.status === 404 ? <NotComputedCard /> : <Alert><AlertTitle>Units breakout unavailable</AlertTitle><AlertDescription>{messageFor(result.error, areaName)}</AlertDescription></Alert> : data && <BreakoutBody
      data={data}
      controls={controls}
      search={search}
      headlineLabel={controls.filter.source ? "Ordered units" : "Units sold"}
      unitsFiltered
      selectedProduct={controls.filter.product}
      header={(conflict) => <>
        <div className="flex flex-wrap items-center gap-3"><h2 className="text-lg font-semibold text-ks-ink">{title}</h2><Button type="button" variant="link" data-nonmutating onClick={clear}>Clear filters</Button></div>
        {!conflict && controls.filter.source && <p className="text-sm text-muted-foreground">ordered units, Amazon from 10 Mar 2026</p>}
      </>}
    />}
  </div>;
}
